<template>
  <b-modal
    size="xl"
    centered
    lazy
    :title="'Importar estoques no armazém ' + warehouse.name"
    header-bg-variant="info"
    footer-border-variant="info"
    v-model="show"
    @hidden="onHidden"
    :hide-footer="true"
  >
    <b-container>

      <div class="row justify-content-between">

        <div class="col-sm-auto col-12">
          <button type="button" class="btn btn-secondary mobile-btn-100" @click="downloadTemplate()" :disabled="loading">
            <i class="fa fa-download"></i> Baixar template
          </button>
        </div>

        <div class="col-sm-auto col-12 mt-sm-0 mt-3 d-flex align-items-center">
          <div class="mr-3">
            <label class="font-weight-bold mb-0">Identificador do endereço:</label>
            <b-form-radio-group v-model="selectedAddressType" name="addressTypeOptions" class="d-inline-block ml-2">
              <b-form-radio v-for="option in addressTypeOptions" :key="option.value" :value="option.value" size="sm">{{ option.label }}</b-form-radio>
            </b-form-radio-group>
          </div>
          <input type="file" ref="importFileInput" style="display: none" @change="importFile">
          <button type="button" class="btn btn-success mobile-btn-100" :disabled="loading" @click="$refs.importFileInput.click()">
            <i class="fa fa-upload"></i> Importar
          </button>
        </div>

      </div>

      <div class="row justify-content-center mt-3" v-if="loading">
        <div class="col-12">
          <b-progress
            :value="sentLinesCount"
            :max="totalLines"
            show-value
            style="height:20px"
            class="bg-secondary"
          >
          </b-progress>
        </div>
        <div class="col-auto mt-3">
          <animation></animation>
        </div>
      </div>

      <div v-if="stockAdjustments && stockAdjustments.length" class="mt-3">
        <h6 class="font-weight-bold mb-3">Ajustes criados:</h6>
        <div v-for="(adjustment, index) in stockAdjustments" class="row py-3 border mx-0 align-items-center" :key="index" :class="{ 'bg-light': index % 2 != 0 }">
          <div class="col-lg col-12">
            <div class="row">
              <div class="col-lg-12 col-sm-6 col-12">
                <span class="font-weight-bold">Número: </span>{{ adjustment.number }}
              </div>
              <div class="col-lg-12 col-sm-6 col-12">
                <span class="font-weight-bold">Estoques: </span>{{ adjustment.totalLines || 0 }}
              </div>
            </div>
          </div>
          <div class="col-lg col-12">
            <div class="row">
              <div class="col-lg-12 col-sm-6 col-12">
                <span class="font-weight-bold">Status: </span>{{ getMovementStatusLabel(adjustment.movementStatus) }}
              </div>
              <div class="col-lg-12 col-sm-6 col-12">
                <span class="font-weight-bold">Data: </span>{{ formatDate(adjustment.createdAt) }}
              </div>
            </div>
          </div>
          <div class="col-lg-auto col-12 mt-lg-0 mt-3">
            <div class="row justify-content-end">
              <div class="col-auto pr-1">
                <a :href="`/orders/location-and-lot-item-adjustment/edit/${adjustment.id}`" target="_blank" class="btn btn-primary">
                  <i class="fa fa-edit"></i> Editar
                </a>
              </div>
              <div class="col-auto pl-1">
                <a :href="`/orders/item-adjustment/auto-fill-revision/${adjustment.id}`" target="_blank" class="btn btn-info">
                  <i class="fa fa-magic"></i> Preencher
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="text-justify mt-3">
        <p>
          Utilize o botão "Baixar template" para baixar um template em CSV.
          Abra-o no excel ou outro editor de planilhas e, caso solicitado, selecione ";" (ponto e vírgula) como separador.
          Preencha os valores das colunas à partir da linha 2. <br>
          Se o estoque atual do sistema for menor do que o informado o sistema irá criar um novo lote com a quantidade informada. <br>
          Já se o estoque atual do sistema for maior do que o informado o sistema irá separar os lotes até atingir a quantidade informada. <br>
          OBS: O preenchimento deve ser feito individualmente por armazém. Ou seja, um arquivo por armazém.
        </p>

        <div class="row font-weight-bold">
          <div class="border" :class="tableColumnSizes[0]">Coluna</div>
          <div class="border" :class="tableColumnSizes[1]">Descrição</div>
          <div class="border" :class="tableColumnSizes[2]">Obrigatório</div>
          <div class="border" :class="tableColumnSizes[3]">Exemplo</div>
        </div>

        <div class="row">
          <div class="border" :class="tableColumnSizes[0]">Empresa</div>
          <div class="border" :class="tableColumnSizes[1]">Código da empresa do item.</div>
          <div class="border" :class="tableColumnSizes[2]">Sim</div>
          <div class="border" :class="tableColumnSizes[3]">0001, 0002</div>
        </div>

        <div class="row">
          <div class="border" :class="tableColumnSizes[0]">Reduzido</div>
          <div class="border" :class="tableColumnSizes[1]">Código reduzido do item.</div>
          <div class="border" :class="tableColumnSizes[2]">Sim</div>
          <div class="border" :class="tableColumnSizes[3]">000001, 000002</div>
        </div>

        <div class="row">
          <div class="border" :class="tableColumnSizes[0]">Descrição</div>
          <div class="border" :class="tableColumnSizes[1]">Descrição do item. Não é usado pelo sistema. Pode ser usado pelo usuário para auxiliar no preenchimento ou ficar em branco.</div>
          <div class="border" :class="tableColumnSizes[2]">Não</div>
          <div class="border" :class="tableColumnSizes[3]">Produto A, Produto B, Em branco</div>
        </div>

        <div class="row">
          <div class="border" :class="tableColumnSizes[0]">Endereço</div>
          <div class="border" :class="tableColumnSizes[1]">UUID, código de barras ou nome do endereço conforme opção selecionada</div>
          <div class="border" :class="tableColumnSizes[2]">Sim</div>
          <div class="border" :class="tableColumnSizes[3]">R01 B02 A03, 20190812094949187, 4ffe4acc-cc83-4e67-996a-65e21f5e1880</div>
        </div>

        <div class="row">
          <div class="border" :class="tableColumnSizes[0]">Quantidade</div>
          <div class="border" :class="tableColumnSizes[1]">Quantidade do estoque a ser importado.</div>
          <div class="border" :class="tableColumnSizes[2]">Sim</div>
          <div class="border" :class="tableColumnSizes[3]">10, 50, 100, 10.5</div>
        </div>

        <p class="mt-3">Após finalizar o preenchimento clique em "Importar" e selecione o arquivo</p>
      </div>

      <confirmation-modal
        id="import-confirmation-modal"
        :msg="'Tem certeza de que deseja importar ' + totalLines + ' estoques?'"
        :ok="confirmImport"
      ></confirmation-modal>

    </b-container>
  </b-modal>
</template>

<script>
import Animation from "@/components/loaders/animation";
import { httpClient } from "@/service";
import shared from '@/shared/shared';
import { ModelListSelect } from 'vue-search-select'
import ConfirmationModal from '@/components/common/confirmation-modal';

const chunkSize = 500;
const template = 'Empresa; Reduzido; Descrição; Endereço; Quantidade'

export default {
  name: 'balance-import-modal',
  components: {
    Animation,
    ModelListSelect,
    ConfirmationModal
  },

  props: {
    onHidden: {
      type: Function,
      default: () => {
        return;
      }
    },
    warehouse: {
      type: Object,
      require
    }
  },

  data() {
    return {
      show: false,
      loading: false,
      selectedAddressType: 'NAME',
      addressTypeOptions: [
        { value: 'UUID', label: 'UUID' },
        { value: 'NAME', label: 'Nome' },
        { value: 'BARCODE', label: 'Código de barras' }
      ],
      tableColumnSizes: [
        'col-lg-2 col-sm-3 col-12',
        'col-lg-6 col-sm-9 col-12',
        'col-lg-2 col-sm-3 col-12',
        'col-lg-2 col-sm-9 col-12'
      ],
      totalLines: 0,
      lines: [],
      chunks: [],
      chunkIndex: 0,
      stockAdjustments: [],
      sentLinesCount: 0
    }
  },

  mounted() {
  },

  computed: {
    importCompleted() {
      return this.sentLinesCount >= this.totalLines;
    }
  },

  methods: {
    showModal() {
      this.show = true;
    },

    hideModal() {
      this.show = false;
    },

    downloadTemplate() {
      shared.downloadString(template, 'text/csv', 'template_importacao_estoques.csv');
    },

    importFile() {
      if (this.$refs.importFileInput
        && this.$refs.importFileInput.files
        && this.$refs.importFileInput.files.length) {
        let f = this.$refs.importFileInput.files[0];
        this.resetFileInput();

        if (!f.name || !f.name.endsWith('.csv')) {
          this.$notify.textError('Arquivo inválido. O tipo do arquivo deve ser CSV.');
          return;
        }

        this.loading = true;
        let reader = new FileReader();

        reader.onload = () => {
          try {
            let content = event.target.result;

            if (content) {
              this.loading = false;
              this.lines = content.split('\n');

              if (!this.lines[0].length) {
                this.lines.splice(0, 1);
              }

              if (!this.lines[this.lines.length - 1].length) {
                this.lines.splice(this.lines.length - 1, 1);
              }

              this.totalLines = this.lines.length - 1;
              this.$bvModal.show('import-confirmation-modal');
            } else {
              this.$notify.textError('Arquivo vazio');
              this.loading = false;
            }
          } catch (e) {
            this.loading = false;
            console.error(e);
            this.$notify.textError('Houve um erro ao importar o arquivo');
          }
        };

        reader.onerror = (error) => {
          this.loading = false;
          console.error(error);
          this.$notify.textError('Houve um erro ao importar o arquivo');
        }

        reader.readAsText(f);
      }
    },

    confirmImport() {
      this.stockAdjustments = [];
      this.chunks = [];
      this.chunkIndex = 0;
      this.sentLinesCount = 0;

      for (let i = 0; i < this.lines.length; i += chunkSize) {
        let chunk = this.lines.slice(i, i + chunkSize);
        this.chunks.push(chunk);
      }

      console.log(this.chunks);
      this.sendNextChunk();
    },

    sendNextChunk() {
      this.loading = true;
      httpClient
      .post(
        `${process.env.VUE_APP_API_URL}balances/import/${this.warehouse.id}/${this.chunkIndex === 0}/${this.selectedAddressType}`,
        this.chunks[this.chunkIndex]
      ).then(({ data }) => {
        this.handleChunckSuccess(data);
      })
      .catch((error) => {
        this.loading = false;
        if (error.message) {
          this.$notify.error(error)
        } else {
          this.$notify.textError("Houve um erro ao importar o arquivo");
        }
      });
    },

    handleChunckSuccess(data) {
      this.loading = false;
      data.data.totalLines = data.meta.totalLines;

      this.$notify.success(data.data.totalLines + ' endereços importados com sucesso no armazém ' + this.warehouse.name);
      //this.$notify.success('Página ' + (this.chunkIndex + 1) + ' importada com sucesso');
      this.chunkIndex++;
      this.stockAdjustments.push(data.data);
      this.sentLinesCount += data.data.totalLines;

      if (this.importCompleted) {
        this.$notify.success('Importação finalizada');
      } else {
        this.sendNextChunk();
      }
    },

    resetFileInput() {
      this.$refs.importFileInput.value = null;
    },

    formatDate(date) {
      return shared.formatDate(date);
    },

    getMovementStatusLabel(status) {
      return shared.getMovementStatusLabel(status);
    }
  }
}
</script>
